import {
  FIXED_STEP,
  SLIME_SEPARATION_SLACK,
} from "../../engine/core/constants";
import type { GameContext } from "../../engine/core/GameContext";
import type { PressureState } from "../pressure/PressureState";
import type { BondState } from "./BondState";

/**
 * 黏连（[决策 0016](../../../../../docs/决策/0013-核心循环改为压力爆浆.md) Q11）：
 * 碰到就粘，绑定有寿命，到期松开，没有别的规则。黏连不是玩法，它只是让紧凑分布撑到
 * 玩家点燃它——成团 = 保压 = 连锁基础。
 *
 * 接触事实来自 `PhysicsSystem` 分离循环顺手输出的接触对（与接触深度同源），这里不再查
 * 一遍网格。每步三段：清理（死亡、换槽、到期）→ 形成 → 拉力。拉力写进速度，由下一步
 * 物理积分，与水滴冲力、溅射推力同一条路。
 */
export class BondSystem {
  constructor(
    private readonly bonds: BondState,
    private readonly pressure: PressureState,
  ) {}

  update(context: GameContext, dt: number): void {
    const { slimes, state, level, catalog } = context;
    const world = level.world;
    const bonds = this.bonds;
    const steps = state.steps;
    // 拉力按充盈比算质量，新生成的槽位要先由压力状态初始化体积。幂等，压力系统稍后再调一次无害。
    this.pressure.syncSlots(context);

    // 一、清理。已标记死亡的仍然 active，要等 DespawnSlime 落地才回收；不排除它们的话，
    // 爆开的那一只会在被清除前的这一步继续把邻居拉向自己。世代比对必须在第二段同步之前，
    // 否则换了占用者的槽位会被当成同一只。
    for (let index = 0; index < bonds.count; index += 1) {
      const a = bonds.a[index];
      const b = bonds.b[index];
      const stale =
        slimes.active[a] === 0 ||
        slimes.active[b] === 0 ||
        slimes.isDying(a) ||
        slimes.isDying(b) ||
        bonds.generation[a] !== slimes.generation[a] ||
        bonds.generation[b] !== slimes.generation[b];
      if (!stale && steps < bonds.until[index]) continue;
      if (!stale) {
        const cooldown = steps + world.bondCooldown / FIXED_STEP;
        bonds.cooldownUntil[a] = cooldown;
        bonds.cooldownUntil[b] = cooldown;
      }
      bonds.removeAt(index);
      index -= 1;
    }

    // 二、槽位换人即重置本只的冷却；它的旧绑定已在上一段按世代不符清掉。
    for (let id = 0; id < slimes.capacity; id += 1) {
      if (bonds.generation[id] === slimes.generation[id]) continue;
      bonds.generation[id] = slimes.generation[id];
      bonds.cooldownUntil[id] = 0;
    }

    // 三、形成。寿命为 0 即关闭黏连。表满或伙伴满按「这一步没粘上」处理。
    // 一对的寿命 = 世界寿命 × 双方黏性的几何平均：任一方黏性为 0 即永不粘。
    if (world.bondLife > 0) {
      for (let index = 0; index < slimes.contactCount; index += 1) {
        const a = slimes.contactA[index];
        const b = slimes.contactB[index];
        if (slimes.isDying(a) || slimes.isDying(b)) continue;
        if (steps < bonds.cooldownUntil[a] || steps < bonds.cooldownUntil[b])
          continue;
        if (bonds.isBonded(a, b)) continue;
        const stickiness = Math.sqrt(
          catalog.slimes[slimes.definition[a]].stickiness *
            catalog.slimes[slimes.definition[b]].stickiness,
        );
        if (stickiness <= 0) continue;
        bonds.add(a, b, steps + (world.bondLife * stickiness) / FIXED_STEP);
      }
    }

    // 四、拉力。只在被拉开时回拉，贴合以内交给分离力，两者不打架。
    // 有效质量与溅射推力同一口径：0.4 + 充盈比，越满越重。
    //
    // 回拉是**带临界阻尼的弹簧**，不是纯弹簧。分离修正只改位置、不消掉相向速度：纯弹簧拉回来的
    // 那股速度会带着两只穿过对方圆心再被弹开，于是粘住的一对互相绕着转（第一关实测每秒 13 圈）。
    // 原型的分离力里有 `-22·vn` 这一项吸掉了它，我们的分离改成位置修正后必须在这里补回来。
    // 阻尼系数取 2·√(k·m)，即临界阻尼——刚好不过冲，也不用再加一个内容参数。
    if (world.bondStrength <= 0) return;
    for (let index = 0; index < bonds.count; index += 1) {
      const a = bonds.a[index];
      const b = bonds.b[index];
      const dx = slimes.x[b] - slimes.x[a];
      const dy = slimes.y[b] - slimes.y[a];
      const distance = Math.hypot(dx, dy) || 0.001;
      const rest = slimes.radius[a] + slimes.radius[b] - SLIME_SEPARATION_SLACK;
      if (distance <= rest) continue;
      const nx = dx / distance;
      const ny = dy / distance;
      const definitionA = catalog.slimes[slimes.definition[a]];
      const definitionB = catalog.slimes[slimes.definition[b]];
      const massA =
        definitionA.mass *
        (0.4 + this.pressure.volume[a] / definitionA.burstVolume);
      const massB =
        definitionB.mass *
        (0.4 + this.pressure.volume[b] / definitionB.burstVolume);
      // 相对速度沿绑定轴的分量，正值为正在分开。
      const vn =
        (slimes.vx[b] - slimes.vx[a]) * nx + (slimes.vy[b] - slimes.vy[a]) * ny;
      // 阻尼按**约化质量**算成冲量，且一步最多把相对速度衰减到零、绝不反向。显式施加 c·vn·dt/m
      // 在轻的一方（气泡质量 0.35）上会超过它自身的速度，把速度反向打过头，弹簧随之发散——
      // 第二关实测成对相对角度每秒转 1000°、峰值速度 3000 px/s，粘住的一对被甩开、连线露出来。
      const reduced = (massA * massB) / (massA + massB);
      const damping = 2 * Math.sqrt(world.bondStrength * reduced);
      const relax = Math.min(1, (damping * dt) / reduced) * vn * reduced;
      const force = world.bondStrength * (distance - rest) * dt + relax;
      slimes.vx[a] += (nx * force) / massA;
      slimes.vy[a] += (ny * force) / massA;
      slimes.vx[b] -= (nx * force) / massB;
      slimes.vy[b] -= (ny * force) / massB;
    }
  }
}
